import { useState, type ReactElement } from "react";
import { useRetainedValue } from "../../hooks/useRetainedValue.js";
import { Link2, Plus, Unlink } from "lucide-react";
import { ConfirmDialog } from "../ConfirmDialog/index.js";
import { cn } from "../../lib/utils.js";
import { initialsOf } from "../../lib/initials.js";
import { Badge } from "../../atoms/Badge/Badge.js";
import { OwnerChip } from "../../atoms/OwnerChip/OwnerChip.js";
import { StatusPill } from "../../atoms/StatusPill/StatusPill.js";
import { Button } from "../../atoms/Button/Button.js";
import { Skeleton } from "../../atoms/Skeleton/Skeleton.js";
import { List, ListItem } from "../List/index.js";
import { EmptyState } from "../EmptyState/EmptyState.js";
import { Alert } from "../Alert/Alert.js";
import { Tooltip } from "../Tooltip/Tooltip.js";

// View-model and props live in `LinkedWorkPanel.types.ts` — the convention this
// folder already sets with `LinkEntitiesModal.types.ts`. Imported for use here
// AND re-exported, so the several `from "./LinkedWorkPanel.js"` imports across
// the package are unchanged.
import {
  NO_PROOF_OF_IMPACT_NOTE,
  START_WORK_KINDS,
  START_WORK_LABEL,
  kindLabel,
  type LinkedWorkPanelProps,
  type LinkedWorkRow,
} from "./LinkedWorkPanel.types.js";

export {
  NO_PROOF_OF_IMPACT_NOTE,
  kindLabel,
  type StartWorkKind,
  type WorkTargetKind,
  type LinkedWorkRow,
  type LinkedWorkPanelProps,
} from "./LinkedWorkPanel.types.js";

/**
 * Data-blind list of execution work linked to a strategy item, with the
 * no-proof-of-impact guarantee stated unconditionally (PB-239).
 *
 * Reused verbatim by the Hoshin item surface, the plan-level roll-up
 * (`canManage={false}`), and later the SOLVE (PB-518) and VSM (PB-519) panels —
 * each supplies its own adapter, so nothing module-specific belongs here.
 */
export function LinkedWorkPanel({
  rows,
  heading = "Linked work",
  canManage = false,
  canUnlink,
  isLoading = false,
  error = null,
  onStartWork,
  startWorkKinds = START_WORK_KINDS,
  onOpenLinkDialog,
  onUnlink,
  rowActionSlot,
  rowTitleSlot,
  hiddenNotes = [],
  className,
}: LinkedWorkPanelProps): ReactElement {
  // The row awaiting confirmation, not a boolean: the dialog names the item, so
  // it needs the row, and holding the row is also what makes a second click on
  // a DIFFERENT row re-target the dialog rather than confirm the first one.
  const [pendingUnlink, setPendingUnlink] = useState<LinkedWorkRow | null>(
    null,
  );

  const unlinking = useRetainedValue(pendingUnlink);

  return (
    <section className={cn("flex flex-col gap-3", className)}>
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-dense-1 font-semibold uppercase tracking-wider text-pb-text-muted">
          {heading}
        </h3>
        <span className="flex flex-wrap items-center gap-2">
          {/* Linking EXISTING work, listed first because it is the commoner
              act and the one the empty state tells the reader to perform. */}
          {canManage && onOpenLinkDialog && (
            <Button variant="secondary" size="sm" onClick={onOpenLinkDialog}>
              <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
              Link work
            </Button>
          )}
          {/* Only the kinds the host offers: a kind whose module is off for
              the workspace is not offered at all (ADR-0100). */}
          {canManage &&
            onStartWork &&
            startWorkKinds.map((kind) => (
              <Button
                key={kind}
                variant="secondary"
                size="sm"
                onClick={() => onStartWork(kind)}
              >
                <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                {START_WORK_LABEL[kind]}
              </Button>
            ))}
        </span>
      </header>

      {isLoading ? (
        <div
          className="space-y-2"
          aria-busy="true"
          aria-label="Loading linked work"
        >
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : error && rows.length === 0 ? (
        <Alert tone="critical">{error}</Alert>
      ) : rows.length === 0 && hiddenNotes.length > 0 ? (
        <HiddenLinkNotes notes={hiddenNotes} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="first-use"
          title="No work linked yet"
          description={
            canManage
              ? "Link a SOLVE or a value-stream map to show the work behind this item."
              : "Related work will appear here when it is linked to this item."
          }
        />
      ) : (
        <>
          {/* A failed REFRESH over rows already held. The error used to replace
              them, so a refetch blip hid work that is linked and still on
              screen a moment ago — and the reader is then told nothing about
              why. Both, or the reader loses one of two true things. */}
          {error ? <Alert tone="critical">{error}</Alert> : null}
          <List divided>
            {rows.map((row) => (
              <ListItem
                key={row.id}
                className="items-start"
                leading={
                  <Badge variant="outline" className="mt-0.5">
                    {kindLabel(row)}
                  </Badge>
                }
                trailing={
                  <span className="flex items-center gap-2">
                    {/* No Open on a row whose module is off (d3); the host's
                        slot is not even asked. */}
                    {row.unavailableReason ? null : rowActionSlot?.(row)}
                    {(canUnlink ?? canManage) && onUnlink && (
                      <Tooltip content={`Unlink ${row.title}`}>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconOnly
                          aria-label={`Unlink ${row.title}`}
                          onClick={() => setPendingUnlink(row)}
                        >
                          <Unlink className="h-3.5 w-3.5" aria-hidden="true" />
                        </Button>
                      </Tooltip>
                    )}
                  </span>
                }
              >
                <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <span
                    className={cn(
                      "font-medium",
                      row.unavailableReason
                        ? "text-pb-text-muted"
                        : "text-pb-text",
                    )}
                  >
                    {(!row.unavailableReason ? rowTitleSlot?.(row) : null) ??
                      row.title}
                  </span>
                  {row.reference && (
                    <span className="text-xs text-pb-text-muted pb-tabular">
                      {row.reference}
                    </span>
                  )}
                  {/* Rendered only when the host supplied one — never fabricated. */}
                  {row.status && (
                    <Tooltip content={`Work status: ${row.status.label}`}>
                      <StatusPill tone={row.status.tone}>
                        {row.status.label}
                      </StatusPill>
                    </Tooltip>
                  )}
                  {row.backlinked && (
                    <Badge variant="outline">Backlinked</Badge>
                  )}
                </span>

                {row.unavailableReason && (
                  <span className="mt-1 block text-xs text-pb-text-muted">
                    {row.unavailableReason}
                  </span>
                )}
                {row.context && (
                  <span className="mt-1 block text-xs text-pb-text-secondary">
                    {row.context}
                  </span>
                )}
                {row.note && (
                  <span className="mt-1 block [overflow-wrap:anywhere] text-xs italic text-pb-text-muted">
                    {row.note}
                  </span>
                )}
                <span className="mt-1.5 flex flex-wrap items-center gap-2">
                  {row.owner && (
                    <OwnerChip
                      initials={initialsOf(row.owner.name)}
                      name={row.owner.name}
                    />
                  )}
                  {row.tags?.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </span>
              </ListItem>
            ))}
          </List>
          {hiddenNotes.length > 0 ? (
            <HiddenLinkNotes notes={hiddenNotes} />
          ) : null}
        </>
      )}

      <p className="text-xs text-pb-text-muted">{NO_PROOF_OF_IMPACT_NOTE}</p>

      {/* Unlinking used to fire on the first click of an icon-only button sat
          next to the row's own controls — the least reversible act on the panel
          behind its smallest target. */}
      {/* The dialog owns its own closing frame (ADR-0091), so the row it is
          confirming is RETAINED: on the frame the selection clears, the title
          would otherwise read off null while the dialog is still painted. */}
      <ConfirmDialog
        open={pendingUnlink !== null}
        title={`Unlink ${unlinking?.title ?? ""}?`}
        confirmLabel="Unlink"
        onClose={() => setPendingUnlink(null)}
        onConfirm={() => {
          const row = pendingUnlink;
          setPendingUnlink(null);
          if (row) onUnlink?.(row.id);
        }}
      >
        Unlinking removes the connection only. Neither the linked item nor this
        plan is deleted, and you can link it again later.
      </ConfirmDialog>
    </section>
  );
}

/**
 * Greyed lines for links whose module is off and whose names live there
 * (ADR-0100). Shared with the dialog's "Already linked" list.
 */
export function HiddenLinkNotes({
  notes,
}: {
  readonly notes: readonly string[];
}): ReactElement {
  return (
    <ul className="flex flex-col gap-1">
      {notes.map((note) => (
        <li key={note} className="text-xs text-pb-text-muted">
          {note}
        </li>
      ))}
    </ul>
  );
}
